/**
 * TxnBulkActionBar.jsx — Phase 3
 *
 * Sticky-bottom bar that appears when the user has selected >=1
 * transactions. Four bulk actions:
 *   • Recategorise (opens a Popover with CategorySelect)
 *   • Mark reviewed (single-shot, no dialog)
 *   • Link to recurring (opens Popover listing user's recurring)
 *   • Delete (AlertDialog confirm)
 *
 * The bar owns none of the mutation logic — it just calls back to the
 * parent's mutate functions. This keeps Transactions.js in charge of
 * how many API calls happen per action (batch vs sequential) and makes
 * the bar trivially testable.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CategorySelect } from '@/components/CategorySelect';
import { X, Tag, CheckCircle2, Repeat, Trash2, Loader2 } from 'lucide-react';

export default function TxnBulkActionBar({
  count,
  onClear,
  onRecategorise,
  onMarkReviewed,
  onLinkRecurring,
  onDelete,
  recurringOptions = [],
  // Phase 84.19.3 — where to render the bar.
  //   'sticky' (default, legacy) → fixed bottom-6, floats above content
  //   'inline' → sits in normal flow. Beta feedback said the sticky one
  //              at the bottom of long ledgers was hard to notice + got
  //              hidden by mobile Safari home indicator. Inline mode
  //              anchors it right next to the Select-All pill.
  variant = 'sticky',
}) {
  const [catOpen, setCatOpen]     = useState(false);
  const [linkOpen, setLinkOpen]   = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(null); // 'recat' | 'review' | 'link' | 'delete' | null

  if (count === 0) return null;

  const withBusy = async (label, fn) => {
    setBusy(label);
    try { await fn(); }
    finally { setBusy(null); }
  };

  // Phase 84.19.6 — sticky offset upgraded from top-14 to top-16 (64 px).
  // Beta feedback: on some viewports the AppShell header sat slightly
  // taller than the previous 56 px so the bar was clipping under it or
  // hidden entirely on scroll-restoration. 64 px gives a small buffer
  // and matches the actual header outer height.
  const wrapperClass =
    variant === 'inline'
      ? 'sticky top-16 z-30 w-full mb-2 pt-1'
      : 'fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-3xl';
  const wrapperStyle =
    variant === 'sticky'
      ? { paddingBottom: 'env(safe-area-inset-bottom, 0px)' }
      : undefined;

  return (
    <div
      className={wrapperClass}
      style={wrapperStyle}
      data-testid="transactions-bulk-action-bar"
      data-variant={variant}
      role="toolbar"
      aria-label={`Bulk actions for ${count} selected transactions`}
    >
      <div
        className="rounded-2xl border border-[#B8862C]/40 bg-[#D9A441] shadow-brand px-1.5 sm:px-3 py-2 flex items-center gap-1 sm:gap-2 text-neutral-900 overflow-x-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="text-xs sm:text-sm font-semibold px-0.5 sm:px-2 shrink-0" data-testid="transactions-bulk-count">
          {count} selected
        </div>

        <div className="flex items-center gap-2 sm:gap-1 ml-auto flex-nowrap shrink-0">
          {/* Recategorise */}
          <Popover open={catOpen} onOpenChange={setCatOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-lg px-2 sm:px-3 text-neutral-900 hover:bg-black/10 hover:text-neutral-900 text-xs sm:text-sm"
                disabled={!!busy}
                aria-label="Recategorise selected transactions"
                data-testid="transactions-bulk-recateg-button"
              >
                {busy === 'recat' ? <Loader2 className="h-4 w-4 sm:mr-1.5 animate-spin" /> : <Tag className="h-4 w-4 sm:mr-1.5" />}
                <span className="hidden sm:inline">Recategorise</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 rounded-2xl p-3" align="end">
              <div className="text-xs font-medium text-muted-foreground mb-2">Pick a category for all {count}</div>
              <CategorySelect
                type="expense"
                value=""
                onChange={async (v) => {
                  setCatOpen(false);
                  await withBusy('recat', () => onRecategorise?.(v));
                }}
                testId="transactions-bulk-recateg-select"
              />
            </PopoverContent>
          </Popover>

          {/* Mark reviewed */}
          <Button
            variant="ghost" size="sm" className="rounded-lg px-2 sm:px-3 text-neutral-900 hover:bg-black/10 hover:text-neutral-900 text-xs sm:text-sm"
            disabled={!!busy}
            onClick={() => withBusy('review', () => onMarkReviewed?.())}
            aria-label="Mark selected as reviewed"
            data-testid="transactions-bulk-mark-reviewed-button"
          >
            {busy === 'review' ? <Loader2 className="h-4 w-4 sm:mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 sm:mr-1.5" />}
            <span className="hidden sm:inline">Mark reviewed</span>
          </Button>

          {/* Link to recurring */}
          <Popover open={linkOpen} onOpenChange={setLinkOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-lg px-2 sm:px-3 text-neutral-900 hover:bg-black/10 hover:text-neutral-900 text-xs sm:text-sm disabled:opacity-50"
                disabled={!!busy || recurringOptions.length === 0}
                aria-label="Link selected to a recurring commitment"
                data-testid="transactions-bulk-link-recurring-button"
              >
                {busy === 'link' ? <Loader2 className="h-4 w-4 sm:mr-1.5 animate-spin" /> : <Repeat className="h-4 w-4 sm:mr-1.5" />}
                <span className="hidden sm:inline">Link</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 rounded-2xl p-2" align="end">
              <div className="text-xs font-medium text-muted-foreground mb-1 px-1">Link {count} to</div>
              <div className="max-h-64 overflow-y-auto">
                {recurringOptions.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={async () => { setLinkOpen(false); await withBusy('link', () => onLinkRecurring?.(r.id)); }}
                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-accent text-sm truncate"
                    data-testid={`transactions-bulk-link-option-${r.id}`}
                  >
                    {r.name}
                  </button>
                ))}
                {recurringOptions.length === 0 && (
                  <div className="px-2 py-3 text-xs text-muted-foreground text-center">No recurring commitments yet.</div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Delete — icon-only on mobile, icon+label on desktop */}
          <Button
            variant="ghost" size="sm"
            className="rounded-lg px-2 sm:px-3 text-red-700 hover:text-red-800 hover:bg-black/10 text-xs sm:text-sm"
            disabled={!!busy}
            onClick={() => setConfirmDelete(true)}
            aria-label="Delete selected transactions"
            data-testid="transactions-bulk-delete-button"
          >
            <Trash2 className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Delete</span>
          </Button>

          {/* Clear — desktop only; mobile users can un-check the pill
              above or tap individual rows */}
          <Button
            variant="ghost" size="sm" className="hidden sm:inline-flex rounded-lg px-2 text-neutral-900 hover:bg-black/10 hover:text-neutral-900"
            onClick={onClear}
            data-testid="transactions-bulk-clear-selection-button"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="transactions-bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {count} {count === 1 ? 'transaction' : 'transactions'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {count === 1 ? 'this transaction' : `these ${count} transactions`} from your ledger.
              Category totals, recurring detections and dashboard charts will update accordingly.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="transactions-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setConfirmDelete(false);
                await withBusy('delete', () => onDelete?.());
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="transactions-bulk-delete-confirm-action"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
